import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { SECTORS, SECTOR_KEYS, SectorChip } from "../lib/sectors";
import { Check, PhoneCall, Megaphone, ArrowRight, CalendarCheck } from "lucide-react";

const fmtDate = (d) => new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export function Today({ onNavigate, allowedSectors, showContacts = true }) {
  const [data, setData] = useState(null);
  const sectors = allowedSectors && allowedSectors.length ? allowedSectors : SECTOR_KEYS;

  const load = () => api.get("/today").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    await api.put(`/contents/${id}`, { completed: true });
    load();
  };

  if (!data) return <div className="p-8 text-slate-400">Caricamento…</div>;

  const totalPublish = sectors.reduce((n, s) => n + (data.to_publish[s]?.length || 0), 0);
  const gridCols = sectors.length === 1 ? "grid-cols-1 max-w-md" : sectors.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="today-view">
      <p className="text-slate-400 text-sm capitalize flex items-center gap-2"><CalendarCheck className="h-4 w-4" /> {fmtDate(data.date)}</p>

      {/* Cosa pubblicare oggi */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="h-5 w-5 text-slate-300" />
          <h2 className="font-display font-bold text-lg">Cosa pubblicare oggi</h2>
        </div>
        {totalPublish === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-slate-400 text-sm">
            Nessun contenuto programmato per oggi. Crea idee dai 3 settori o pianifica nel Calendario.
          </div>
        ) : (
          <div className={`grid grid-cols-1 gap-5 ${gridCols}`}>
            {sectors.map((sk) => {
              const S = SECTORS[sk]; const items = data.to_publish[sk] || [];
              return (
                <div key={sk} className="glass rounded-2xl p-5" style={{ borderTop: `3px solid ${S.hex}` }} data-testid={`today-publish-${sk}`}>
                  <div className="flex items-center justify-between mb-3">
                    <SectorChip sector={sk} />
                    <span className="text-xs text-slate-500">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.length === 0 && <p className="text-xs text-slate-500">Niente per oggi</p>}
                    {items.map((c) => (
                      <div key={c.id} className="flex items-start gap-2 p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
                        <button data-testid={`today-complete-${c.id}`} onClick={() => complete(c.id)}
                          className="mt-0.5 h-5 w-5 shrink-0 rounded-md border border-white/20 grid place-items-center hover:border-white/50 transition-colors">
                          <Check className="h-3 w-3 opacity-0 hover:opacity-100" />
                        </button>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.title}</p>
                          <p className="text-[11px] text-slate-500 capitalize">{c.type} · {c.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Chi ricontattare oggi */}
      {showContacts && (
      <section>
        <div className="flex items-center gap-2 mb-4">
          <PhoneCall className="h-5 w-5 text-slate-300" />
          <h2 className="font-display font-bold text-lg">Chi ricontattare oggi</h2>
        </div>
        {data.to_contact.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-slate-400 text-sm">
            Nessun check-in in scadenza. Aggiungi clienti in <button onClick={() => onNavigate("wellness")} className="text-emerald-400 underline">Business Wellness</button> per attivare i promemoria (giorno 3, 7, 14, 21).
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.to_contact.map((c) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="glass rounded-2xl p-4 flex items-center justify-between" style={{ borderLeft: "3px solid #10B981" }} data-testid={`today-contact-${c.id}`}>
                <div>
                  <p className="font-semibold">{c.name}</p>
                  {c.program && <p className="text-xs text-slate-500">{c.program}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {c.pending.map((p) => (
                      <span key={p.day} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                        Check-in giorno {p.day}{p.overdue_days > 0 ? ` · +${p.overdue_days}g` : ""}
                      </span>
                    ))}
                  </div>
                </div>
                <button onClick={() => onNavigate("wellness")} className="shrink-0 ml-2 h-9 w-9 grid place-items-center rounded-xl bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors">
                  <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
}
